import * as React from "react";
import { useTheme } from "@/hooks/useTheme";
import FormBlock from "@/components/molecules/FormBlock";
import form, { ConnectingDetail, Connecting } from "@/utils/form";
import { connectingTypeOptions } from "@/constants/ConnectingType";
import { connectingRoadWidthGroupOptions } from "@/constants/ConnectingRoadWidthGroup";
import { connectingRoadContactWidthGroupOptions } from "@/constants/ConnectingRoadContactWidthGroup";
import { connectingDirectionOptions } from "@/constants/ConnectingDirection";
import FormContext, { initFormStore, updateFormValues } from "@/contexts/Form";
import { validateConnecting as connectingValidator } from "@/utils/validation";
import RoadConnectingInfo from "./roadConnectingInfo";
import MediaQueryContext, { initMediaQueryStore } from "@/contexts/MediaQuery";

const ConnectingDetails = ({
  isNewTheme,
  connecting,
  validateConnectingDetails,
  setConnecting,
  setValidateConnectingDetails,
}: {
  isNewTheme: boolean;
  connecting: Connecting;
  validateConnectingDetails: ConnectingDetail[];
  setConnecting: React.Dispatch<React.SetStateAction<Connecting>>;
  setValidateConnectingDetails: React.Dispatch<React.SetStateAction<ConnectingDetail[]>>;
}) => {
  const { COLOR_THEME } = useTheme();
  const { dispatch } = React.useContext(FormContext) || initFormStore(form);
  const { current: range } = React.useContext(MediaQueryContext) || initMediaQueryStore("md");

  const handleChangeConnectingDetails = (
    index: number,
    key: "type" | "direction" | "width" | "contactWidth" | "widthGroup" | "contactWidthGroup",
    detail: ConnectingDetail
  ) => (ev: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { value } = ev.target;
    const details = [...connecting.details];
    const newState = {
      ...detail,
      [key]: value,
    };
    details[index] = newState;
    setConnecting(() => ({
      ...connecting,
      details,
    }));
    const newValidateDetails = [...validateConnectingDetails];
    const newValidateDetail = {
      ...newValidateDetails[index],
      [key]: connectingValidator[key](value),
    };
    newValidateDetails[index] = newValidateDetail;
    setValidateConnectingDetails(newValidateDetails);
  };
  const [isContactOpen, setContactOpen] = React.useState(false);

  const handleInfoIconClick = () => {
    setContactOpen(true);
  };

  React.useEffect(() => {
    dispatch(
      updateFormValues({
        values: {
          connecting,
        },
        validates: {
          connecting: validateConnectingDetails.map(v => Object.values(v).join("")).join(""),
        },
      })
    );
  }, [connecting]);

  const widthAndHeightDetails = (i: number, d: ConnectingDetail) => {
    if (isNewTheme === true) {
      return (
        <React.Fragment>
          <FormBlock.Row>
            <FormBlock.Select
              description="道路幅"
              aria-label="道路幅"
              options={connectingRoadWidthGroupOptions}
              width="200px"
              className="width200px"
              onChange={handleChangeConnectingDetails(i, "widthGroup", d)}
              value={d.widthGroup}
              required
              validateMessage={validateConnectingDetails[i].widthGroup}
              background={COLOR_THEME.extras?.invalid_state?.background}
            />
          </FormBlock.Row>
          <FormBlock.Row>
            <FormBlock.Select
              description="間口"
              aria-label="間口"
              options={connectingRoadContactWidthGroupOptions}
              width="200px"
              className="width200px"
              onChange={handleChangeConnectingDetails(i, "contactWidthGroup", d)}
              value={d.contactWidthGroup}
              required
              validateMessage={validateConnectingDetails[i].contactWidthGroup}
              background={COLOR_THEME.extras?.invalid_state?.background}
            />
          </FormBlock.Row>
        </React.Fragment>
      );
    }

    return (
      <React.Fragment>
        <FormBlock.Row>
          <FormBlock.Input
            description="接道幅員"
            aria-label="接道幅員"
            after="ｍ"
            placeholder="例）1"
            width="124px"
            className="width100px"
            onChange={handleChangeConnectingDetails(i, "width", d)}
            value={d.width}
            required
            validateMessage={validateConnectingDetails[i].width}
            background={COLOR_THEME.extras?.invalid_state?.background}
          />
        </FormBlock.Row>
        <FormBlock.Row>
          <FormBlock.Input
            description="接道接面"
            aria-label="接道接面"
            placeholder="例）1"
            after="ｍ"
            width="124px"
            className="width100px"
            onChange={handleChangeConnectingDetails(i, "contactWidth", d)}
            value={d.contactWidth}
            required
            validateMessage={validateConnectingDetails[i].contactWidth}
            background={COLOR_THEME.extras?.invalid_state?.background}
          />
        </FormBlock.Row>
      </React.Fragment>
    );
  };

  const getInfoIconStyles = () => {
    if (isNewTheme && range === "sm") {
      return { width: "100%" };
    } else {
      return {};
    }
  };

  const renderConnectingDetails = () => {
    return connecting.details.map((d, i) => {
      return (
        <FormBlock className="underline form" key={`${i + 1}`} backgroundColor="#ffffff">
          <FormBlock.Label
            value={`接道${i + 1}`}
            required
            style={getInfoIconStyles()}
            background={COLOR_THEME.extras?.required_icon?.background}
            text={COLOR_THEME.extras?.required_icon?.text}
            border={COLOR_THEME.extras?.required_icon?.border}
            modalData="contact"
            onInfoIconClick={handleInfoIconClick}
          />
          <FormBlock.Row>
            <FormBlock.Select
              description="接道種別"
              aria-label="接道種別"
              options={connectingTypeOptions}
              width="200px"
              className="width200px"
              onChange={handleChangeConnectingDetails(i, "type", d)}
              value={d.type}
              required
              validateMessage={validateConnectingDetails[i].type}
              background={COLOR_THEME.extras?.invalid_state?.background}
            />
          </FormBlock.Row>
          <FormBlock.Row>
            <FormBlock.Select
              description="接道方向"
              aria-label="接道方向"
              width="200px"
              className="width200px"
              options={connectingDirectionOptions}
              onChange={handleChangeConnectingDetails(i, "direction", d)}
              value={d.direction}
              required
              validateMessage={validateConnectingDetails[i].direction}
              background={COLOR_THEME.extras?.invalid_state?.background}
            />
          </FormBlock.Row>
          {widthAndHeightDetails(i, d)}
        </FormBlock>
      );
    });
  };

  return (
    <React.Fragment>
      {renderConnectingDetails()}
      <RoadConnectingInfo open={isContactOpen} onCloseClick={() => setContactOpen(false)} />
    </React.Fragment>
  );
};

export default ConnectingDetails;
